import React from "react";
import {autobind} from "core-decorators";
import {computed, runInAction} from "mobx";
import {observer} from "mobx-react";
import {Form, Input, message, Modal, Select} from "antd";
import type {FormInstance} from "antd/es/form";
import {gql} from "@apollo/client";
import _compact from "lodash/compact";
import _isEmpty from "lodash/isEmpty";
import _uniq from "lodash/uniq";

import {PiecesQuery, PieceUpdate, PieceUpdateVariables} from "@/graphql/types";
import StoreContext from "@/stores/StoreContext";

export interface IEditPieceModalProps {
  close: () => void;
  open: boolean;
  piece: PiecesQuery["pieces"][0];
  pieces: PiecesQuery["pieces"];
}

@autobind
@observer
export default class EditPieceModal extends React.Component<IEditPieceModalProps> {
  static contextType = StoreContext;
  context!: React.ContextType<typeof StoreContext>;

  formRef = React.createRef<FormInstance>();

  @computed({keepAlive: true})
  get folders() {
    return _uniq(_compact(this.props.pieces.map(({folder}) => folder))).sort();
  }

  onFolderChange(folder: string) {
    const form = this.formRef.current!;
    if (folder === "__new__") form.setFieldValue("folderName", "");
    else form.setFieldValue("folderName", folder);
  }

  onFinish() {
    this.updatePiece(); // do not wait for this, we are doing optimistic UI update
    this.props.close();
  }

  onOk() {
    this.formRef.current!.submit();
  }

  async updatePiece() {
    const {piece, pieces} = this.props;
    const {title: previousTitle, composer: previousComposer, folder: previousFolder} = piece;
    const previousEnsembleId = this.context.session.selectedEnsemble?.id;
    const form = this.formRef.current!;
    const title = form.getFieldValue("title");
    const composer = form.getFieldValue("composer");
    // Extract the correct folder string to use
    const folderSelect = form.getFieldValue("folderSelect");
    const folderName = form.getFieldValue("folderName");
    let folder: string | null;
    if (folderSelect === "__null__") folder = null;
    else if (folderSelect === "__new__") folder = folderName;
    else folder = folderSelect;
    const ensembleId = form.getFieldValue("ensembleId") ?? previousEnsembleId;
    const pieceIdx = pieces.indexOf(piece);

    // Optimistic UI update
    runInAction(() => {
      piece.title = title;
      piece.composer = composer;
      piece.folder = folder;
      if (ensembleId !== previousEnsembleId) pieces.splice(pieceIdx, 1);
    });
    try {
      const result = await this.context.apollo.client.mutate<PieceUpdate, PieceUpdateVariables>({
        mutation: gql`
          mutation PieceUpdate($id: Int!, $title: String!, $composer: String!, $folder: String, $choirId: Int) {
            piece: update_pieces_by_pk(
              pk_columns: {id: $id}
              _set: {title: $title, composer: $composer, folder: $folder, choir_id: $choirId}
            ) {
              id
              title
              composer
              folder
              choir_id
            }
          }
        `,
        variables: {id: piece.id, title, composer, folder, choirId: ensembleId},
      });
      const updatedPiece = result.data?.piece;
      if (!updatedPiece) throw new Error("Could not update the Piece sucessfully");
      // Set the DB values (they should be the same)
      runInAction(() => {
        piece.title = updatedPiece.title;
        piece.composer = updatedPiece.composer;
        piece.folder = updatedPiece.folder;
        if (updatedPiece.choir_id !== ensembleId) console.error("Ensemble ID mismatch");
      });
    } catch (error) {
      console.error(error);
      message.error("Oops! Error while updating the piece");
      // Undo optimistic UI update
      runInAction(() => {
        piece.title = previousTitle;
        piece.composer = previousComposer;
        piece.folder = previousFolder;
        if (ensembleId !== previousEnsembleId) pieces.splice(pieceIdx, 0, piece);
      });
    }
  }

  public render() {
    const {session} = this.context;
    const {close, piece, open} = this.props;
    const {title, composer, folder} = piece;
    const folderSelect = folder || "__null__";
    const ensembleId = session.selectedEnsemble?.id;

    return (
      <Modal
        className="EditPieceModal"
        title="Edit piece"
        open={open}
        onCancel={close}
        onOk={this.onOk}
        okText="Update"
        destroyOnClose={true}
        forceRender={true}
      >
        <Form
          name="edit-piece"
          ref={this.formRef}
          initialValues={{title, composer, folderSelect, folderName: folderSelect, ensembleId}}
          onFinish={this.onFinish}
          layout="vertical"
        >
          <Form.Item name="title" label="Title" rules={[{required: true}]}>
            <Input />
          </Form.Item>
          <Form.Item name="composer" label="Composer" rules={[{required: true}]}>
            <Input />
          </Form.Item>
          <Form.Item name="folderSelect" label="Folder" rules={[{required: true}]}>
            <Select onChange={this.onFolderChange}>
              <Select.Option value="__null__">No folder assigned</Select.Option>
              <Select.Option value="__new__">Create a new folder</Select.Option>
              {!_isEmpty(this.folders) && (
                <Select.OptGroup key="your-folders" label="Your folders">
                  {this.folders.map((folder) => (
                    <Select.Option key={folder} value={folder}>
                      {folder}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              )}
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.folderSelect !== currentValues.folderSelect}
          >
            {({getFieldValue}) => {
              const folderSelect = getFieldValue("folderSelect");
              const showFolderName = folderSelect === "__new__";
              return (
                <Form.Item
                  name="folderName"
                  label="New folder name"
                  rules={[{required: showFolderName, message: "Please introduce a folder name"}]}
                  hidden={!showFolderName}
                >
                  <Input />
                </Form.Item>
              );
            }}
          </Form.Item>
          {session.isEnsembleOwner && session.ensemblesOwned.length > 1 && (
            <Form.Item name="ensembleId" label="Ensemble" rules={[{required: true}]}>
              <Select>
                {session.ensemblesOwned.map((ensemble) => (
                  <Select.Option key={ensemble.id} value={ensemble.id}>
                    {ensemble.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    );
  }
}
